import {
	AccessEventAction,
	AuthEventAction,
	DealEventCategory,
	DealEventSource,
	DealEventStatus,
} from '@dealstack/shared/lib/backendClient';
import { DEAL_EVENT_STATUSES_BY_CATEGORY } from '@dealstack/shared/lib/common/business/dealEvent';
import { DealEventLogTab } from '@dealstack/frontend/app/pages/Project/DealEventLog/DealEventLog.types.ts';
import {
	formatAccessHeading,
	formatAuthHeading,
} from '@dealstack/frontend/app/pages/Project/DealEventLog/dealEventLog.utils.ts';
import { getStatusLabel } from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogStatus.utils.ts';
import { DEAL_EVENT_LOG_TAB_CATEGORIES } from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogQuery.utils.ts';

type Translate = (key: string) => string;

export interface DealEventFilterOption {
	value: string;
	label: string;
}

// The shared map is keyed by the business enums; the table speaks the generated unions, whose
// members carry the same strings. Widening to string lets the two meet without a cast.
const REACHABLE_BY_CATEGORY: Record<string, string[]> = DEAL_EVENT_STATUSES_BY_CATEGORY;

/** Every status the tab's categories can report; ALL_EVENTS spans the lot. */
const getTabStatuses = (tab: DealEventLogTab): DealEventStatus[] => {
	const categories = DEAL_EVENT_LOG_TAB_CATEGORIES[tab] ?? Object.values(DealEventCategory);
	const reachable = new Set(
		categories.flatMap((category) => REACHABLE_BY_CATEGORY[category] ?? [])
	);

	return Object.values(DealEventStatus).filter((status) => reachable.has(status));
};

export const getDealEventStatusOptions = (
	tab: DealEventLogTab,
	t: Translate
): DealEventFilterOption[] =>
	getTabStatuses(tab).map((value) => ({ value, label: getStatusLabel(value, t) }));

export const getDealEventCategoryOptions = (t: Translate): DealEventFilterOption[] => [
	{ value: DealEventCategory.PARTICIPANT, label: t('deal_event_log.tab.investor_activity') },
	{ value: DealEventCategory.CAPITAL, label: t('deal_event_log.tab.capital_events') },
	{ value: DealEventCategory.ACCESS, label: t('deal_event_log.tab.team_and_access') },
	{ value: DealEventCategory.MESSAGE, label: t('deal_event_log.tab.messaging') },
];

export const getDealEventChannelOptions = (t: Translate): DealEventFilterOption[] => [
	{ value: DealEventSource.EMAIL_SENT, label: t('deal_event_log.filter.channel.email') },
];

/** Access and auth rows share the tab, and the feed names their types the same way it does. */
export const getDealEventAccessTypeOptions = (t: Translate): DealEventFilterOption[] => [
	...Object.values(AccessEventAction).map((action) => ({
		value: `${DealEventSource.ACCESS}_${action}`,
		label: formatAccessHeading(action, t),
	})),
	...Object.values(AuthEventAction).map((action) => ({
		value: `${DealEventSource.AUTH}_${action}`,
		label: formatAuthHeading(action, t),
	})),
];
